<template>
    <div class="trips-table-wrap">

        <TripModal :show-modal-on="['EditTrip', 'AddTrip']" @created="onTripCreated" @updated="onTripUpdated" @deleted="removeTripFromList" />

        <FilterBar
            v-if="filterBar"
            cookieName="TripFilterOptionsVue"
            :mainFilters="['SearchFilter', 'DateRangeFilter']"
            :secondaryFilters="['TripCustomerFilter', 'TripTypeFilter', 'TransportTypeFilter']"
            filterBarType="trips"
            @change="filtersChanged"
        >
            <template v-slot:right-bar>
                <TotalAmountTrip ref="totalamounttrip" />
            </template>
        </FilterBar>
        <div class="content--inner">
            <b-table
                :fields="tableFields"
                :items="tripTableItems"
                :tbody-tr-class="rowClass"
                :class="['trips-table gk-table gk-table--responsive gk-table--striped gk-table--hover', { 'gk-table--headed': selectableRows }]"
                :key="tripTableKey"
                @row-clicked="rowClicked"
                :busy="loading"
                show-empty
                table-busy
            >
                <template #empty="">
                    <EmptyState :class="{ 'emptystate-simple': componentOnModal }">
                        <template v-if="!componentOnModal" v-slot:image>
                            <img src="/static/img/empty-states/empty-state-cost.png">
                        </template>
                        <template v-slot:title>
                            {{ $gettext('We couldn\'t find any trips') }}
                        </template>
                        <template v-slot:content>
                            {{ $gettext('Start by creating a new one or try updating your filters.') }}
                        </template>
                        <template v-if="!componentOnModal" v-slot:cta>
                            <GekkoButton 
                                :text="$gettext('Add a new tip')"
                                :to="{ name: 'AddTrip' }"
                                icon="fa-solid fa-plus"
                            />
                        </template>
                    </EmptyState>
                </template>
                <template #table-busy="">
                    <LoadingSpinner min-height />
                </template>

                <template v-slot:head(selector)="">
                    <b-form-checkbox v-model="allItemsSelected" @change="selectAllTableItems" />
                </template>

                <template v-if="selectableRows" #cell(selector)="row">
                    <b-form-checkbox v-model="row.item.selected" @change="selectedTableItem" />
                </template>

                <template #cell(date)="data">
                    <div>
                        {{data.item.date | formatDate}}
                    </div>
                </template>

                <template #cell(end_odometer)="data">
                    <div>
                        <font-awesome-icon v-if="data.item.transport_type == 'train'" icon="fa-solid fa-train" />
                        <font-awesome-icon v-if="data.item.transport_type == 'bike'" icon="fa-solid fa-bicycle" />
                        <font-awesome-icon v-if="data.item.transport_type == 'car'" icon="fa-solid fa-car" />
                        <span v-if="data.item.transport_type == 'car'"> {{ odometerText(data.item.end_odometer) }} km</span>
                    </div>
                </template>

                <template #cell(emoji)>
                    <div>
                        <font-awesome-icon icon="fa-solid fa-arrow-right-long" />
                    </div>
                </template>

                <template #cell(total_distance)="data">
                    <div>
                        <div>{{getDistanceText(data.item.total_distance)}} km</div>
                        <div class="duration-text">{{getDurationText(data.item)}}</div>
                    </div>
                </template>

                <template #cell(avatar)="data">
                    <div class="avatar-col pr-0 float-right">
                        <Avatar :userId="data.item.created_by" />
                    </div>
                </template>

                <template slot="bottom-row" v-if="nextUrl">
                    <td :colspan="fields.length" class="table-load-more">
                        <GekkoButton 
                            v-if="nextUrl && !fetchingData" 
                            @click="handleScroll()"
                            id="load_more_button" 
                            class="text-info"
                            variant="link" 
                            :text="$gettext('Load more trips')" 
                        />
                        <LoadingSpinner v-if="fetchingData" />
                    </td>
                </template>

            </b-table>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import TripService from "@/store/services/trip.service";
import GekkoButton from "@/components/GekkoButton";
import TripModal from "@/components/trips/TripModal";
import FilterBar from "@/components/list_filters/FilterBar";
import Avatar from "@/components/Avatar.vue";
import EmptyState from "@/components/ui/EmptyState"
import TotalAmountTrip from "@/components/trips/TotalAmountTrip";
import LoadingSpinner from "@/components/dashboard/ui/LoadingSpinner"

export default {
    name: 'TripTable',
    components: { GekkoButton, TripModal, FilterBar, Avatar, EmptyState, TotalAmountTrip, LoadingSpinner },
    props: {
        selectableRows: {
            type: Boolean,
            default: false
        },
        componentOnModal: {
            type: Boolean,
            default: false
        },
        extended: {
            type: Boolean,
            default: false
        },
        excludeTableFields: {
            type: Array,
            default: () => []
        },
        filterBar: {
            type: Boolean,
            default: true
        }
    },
    data () {
        return {
            loading: true,
            filters: {},
            fields: [
                { key: "date", label: this.$gettext('Date'), tdClass: 'col-date' },
                { key: "end_odometer", label: this.$gettext('Final odometer'), tdClass: "odometer-field" },
                { key: "start_name", label: this.$gettext('From') },
                { key: "emoji", label: this.$gettext('To') },
                { key: "end_name", label: false },
                { key: "total_distance", label: this.$gettext('Total distance'), thClass: 'text-right', tdClass: "text-right" },
                { key: "avatar", label: this.$gettext('User'), tdClass: "avatar-table-field" }
            ],
            tripTableItems:[],
            tripTableKey: 0,
            nextUrl: false,
            fetchingData: false,
            allItemsSelected: false
        }
    },
    computed: {
        ...mapGetters({ 
            getCompanyId: 'main/getCompanyId'
        }),
        selectedTableItems () {
            let selected = null
            if (this.tripTableItems) {
                selected =  this.tripTableItems.filter(item => item.selected)
            }
            return selected
        },
        tableFields () {
            return this.fields.filter(field => !this.excludeTableFields.includes(field.key));
        }
    },
    created() {
        this.filters.extended = 'True';
        window.addEventListener('scroll', this.handleScroll);

        if (this.selectableRows) {
            this.fields.unshift({ key: 'selector', label: '' })
        }
    },
    mounted () {
        this.$emit('mounted', false)
    },
    destroyed () {
        window.removeEventListener('scroll', this.handleScroll);
        if (this.componentOnModal) {
            this.$emit('destroyed')
        }
    },
    methods: {
        getTrips(filters) {
            this.loading = true
            this.fetchingData = false

            if (this.extended) filters.extended = true

            TripService.listPaginated(filters).then((response) => {
                this.tripTableItems = response.data.results
                this.tripTableItems.forEach(item=> {
                    item.start_name = item.start.name
                    item.end_name = item.end.name
                })

                this.nextUrl = response.data.next
                this.loading = false
                this.fetchingData = false
                if (this.selectableRows) this.$emit('get-trips', response.data.count)
            });
        },
        rowClicked(item) {
            if (!this.componentOnModal) {
                this.$router.push({name: 'EditTrip', params: {tripId: item.id}});
            } else {
                if (this.selectableRows) {
                    const trip_index = this.tripTableItems.findIndex(trip => trip.id === item.id)
                    this.$set(this.tripTableItems, trip_index, { ...this.tripTableItems[trip_index], selected: !item.selected })
                    this.selectedTableItem()
                } else {
                    this.$emit('row-clicked', item)
                }
            }
        },
        selectAllTableItems () {
            this.tripTableItems.forEach((trip, trip_index) => {
                this.$set(this.tripTableItems, trip_index, { ...trip, selected: this.allItemsSelected })
            });
            this.selectedTableItem()
        },
        selectedTableItem () {
            this.tripTableItems.splice() // ugly hack for triggering computed property update
            this.$emit('selected-item', this.selectedTableItems)
        },
        onTripCreated(val) {
            let item = val;
            item.start_name = val.start.name;
            item.end_name = val.end.name;

            let dateObj = new Date(val.date);
            let startDateObject = new Date(this.filters.start_date);
            let endDateObject = new Date(this.filters.end_date);

            if (
                (this.filters.type == val.type || this.filters.type == null || !this.filters.type) &&
                ((this.filters.transport_type == val.transport_type || this.filters.transport_type == val.car) || this.filters.transport_type == null || !this.filters.transport_type) &&
                (this.filters.customer == val.customer || !this.filters.customer) &&
                ((!this.filters.start_date || !this.filters.end_date) || (startDateObject < dateObj && endDateObject > dateObj))
            ) {
                this.tripTableItems.unshift(item);
            }

            this.$refs.totalamounttrip.getTotalAmounts();  // update values
        },
        onTripUpdated(val) {
            for (var i in this.tripTableItems) {
                if (this.tripTableItems[i].id === val.id) {
                    this.tripTableItems[i] = val;
                    this.tripTableItems[i].start_name = val.start.name;
                    this.tripTableItems[i].end_name = val.end.name;
                    break;
                }
            }

            this.tripTableItems.map(trip => {
                if (trip.id === val.id) {
                    if (this.filters.type != val.type && this.filters.type != null && this.filters.type) {
                        this.removeTripFromList(trip.id);
                    } if (this.filters.transport_type != val.transport_type && this.filters.transport_type != null && this.transport_type) {
                        if (this.filters.transport_type != val.car)
                            this.removeTripFromList(trip.id);
                    } if (this.filters.customer != val.customer && this.filters.customer) {
                        this.removeTripFromList(trip.id);
                    } if (this.filters.start_date && this.filters.end_date) {
                        let dateObj = new Date(val.date);
                        let startDateObject = new Date(this.filters.start_date);
                        let endDateObject = new Date(this.filters.end_date);

                        if (startDateObject > dateObj || endDateObject < dateObj)
                            this.removeTripFromList(trip.id);
                    }
                }
            });

            this.tripTableKey++;
            this.$refs.totalamounttrip.getTotalAmounts();  // update values
        },
        removeTripFromList(tripId) {
            this.$store.commit("filterStore/SET_UPDATE_FILTER_TRIPS");
            this.tripTableKey++;
            this.tripTableItems = this.tripTableItems.filter(trip => trip.id !== tripId);
        },
        filtersChanged(filters) {
            filters.extended = 'True';
            filters.company_profile = this.getCompanyId;
            filters.search_by_name = filters.search;
            this.filters = filters;
            this.getTrips(filters);
        },
        isInViewport(element_id) {
            let element = document.getElementById(element_id)
            let rect = element.getBoundingClientRect();
            let html = document.documentElement;
            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || html.clientHeight) &&
                rect.right <= (window.innerWidth || html.clientWidth)
            );
        },
        handleScroll() {
            if (!this.fetchingData && this.nextUrl) {
                let loadNewTrips = this.isInViewport('load_more_button');
                if (loadNewTrips) {
                    this.fetchingData = true
                    TripService.listNextPage(this.nextUrl).then((response) => {
                        this.fetchingData = false
                        this.nextUrl = response.data.next
                        this.tripTableItems = this.tripTableItems.concat(response.data.results)
                        this.tripTableItems.forEach(item => {
                            item.start_name = item.start.name
                            item.end_name = item.end.name
                        });
                        if (this.selectableRows) this.$emit('get-trips', response.data.count)
                    });
                }
            }
        },
        getDurationText(item) {
            let floatDuration = parseFloat(item.total_hours);
            let hour = parseInt(floatDuration);
            let minute = ((floatDuration - hour) * 60).toFixed(); // integer value (no seconds allowed)
            if ((hour == 0 && minute == 0) || (!hour || !minute)) {
                if (hour)
                    return hour + "h";
                else if (minute && minute != "NaN")
                    return minute + "m";
                return "-";
            }
            return hour + "h " + minute + "m";
        },
        odometerText(odometer) {
            if(!odometer){
                return "undefined";
            }
            let km = odometer.slice(0,-2);  // remove the .0 decimal
            if (km.length < 6) {  // display always 6 digits
                let nExtraDigits = 6 - km.length;
                let text = "";
                for (var i = 0; i < nExtraDigits; i++) {
                    text = text + "0";
                }
                text = text + km;
                return text;
            } else if (km.length > 6) {
                return km.slice(0, 6);
            } else
                return km;
        },
        rowClass(item){
            if (!item) {
                return '';
            }
            let item_classes = 'costs-table-row cost-complete'
            if(item && item.selected) {
                item_classes += ' item-selected'
            }
            return item_classes
        },
        getDistanceText(totalDistance) {
            let floatNum = parseFloat(totalDistance);
            return floatNum.toFixed(1);
        }
    }
}
</script>

<style scoped>
.avatar-col {
    min-width: 50px;
    align-self: center;
}

.avatar-color{
    background-color: #aaa;
}

.duration-text {
    color: #9A9A9A;
    font-size: 0.7em;
}
</style>

<style lang="scss">
.trips-table-wrap {
    .content--inner {
        min-height: 300px;
    }
    .trips-table {
        .col-date { width: 1%; white-space: nowrap; }
        .avatar-table-field {
            min-width: 75px;
            width: 5%;
            transform: translateY(-10%);
        }
        .odometer-field {
            white-space: nowrap; 
        }
    }
}

</style>
